import { z } from 'zod';
import { ReportBudgetFilterTypeEnum } from '../report-budget.schema';
import { END_BEFORE_START_MESSAGE, INVALID_DATE_MESSAGE, MISSING_PERIOD_MESSAGE, isDateOnlyString } from '../../helpers/date-helpers';

// ─────────────────────────────────────────────────────────────────────────────
// Exportação assíncrona de relatórios (MF-1143). Ver docs/features/report-exports.md.
// ─────────────────────────────────────────────────────────────────────────────

/** Relatórios que já geram o arquivo pelo worker. Os demais continuam na exportação síncrona. */
export const REPORT_EXPORT_TYPES = ['cash_flow', 'nfe', 'budget', 'plans', 'finance_history', 'receivables', 'payables', 'transfers', 'procedures'] as const;
export type ReportExportType = (typeof REPORT_EXPORT_TYPES)[number];

export const REPORT_EXPORT_FORMATS = ['pdf', 'xlsx'] as const;
export type ReportExportFormat = (typeof REPORT_EXPORT_FORMATS)[number];

/**
 * - `queued`: aguardando vaga no worker.
 * - `processing`: um worker reivindicou e está gerando.
 * - `retrying`: falha transitória; volta para a fila em `next_attempt_at`.
 * - `cancelling`: cancelamento pedido durante o processamento; o worker ainda não parou.
 * - `cancelled`, `completed`, `failed`: finais.
 * - `expired`: estava `completed`, mas o arquivo passou do prazo de disponibilidade.
 */
export const REPORT_EXPORT_STATUSES = ['queued', 'processing', 'retrying', 'cancelling', 'cancelled', 'completed', 'failed', 'expired'] as const;
export type ReportExportStatus = (typeof REPORT_EXPORT_STATUSES)[number];

/** Estados que ainda ocupam fila ou worker: contam para os limites de exportações pendentes. */
export const REPORT_EXPORT_ACTIVE_STATUSES: readonly ReportExportStatus[] = ['queued', 'processing', 'retrying', 'cancelling'];

/** Etapas reais do processamento, na ordem em que o worker passa por elas. */
export const REPORT_EXPORT_STEPS = ['validating', 'querying', 'generating', 'storing'] as const;
export type ReportExportStep = (typeof REPORT_EXPORT_STEPS)[number];

/** `YYYY-MM-DD` válido de calendário (rejeita 2026-02-30). A regra é a mesma das rotas de listagem. */
const isoDate = z.string({ required_error: MISSING_PERIOD_MESSAGE }).refine(isDateOnlyString, INVALID_DATE_MESSAGE);

/** Mesmos filtros que a tela do Fluxo de Caixa manda para `/api/finance/cashflow/pdf|xls`. */
export const CashFlowExportFiltersSchema = z
	.object({
		start_date: isoDate,
		end_date: isoDate,
		type: z.enum(['ALL', 'INCOME', 'EXPENSE']).optional(),
		payment_method: z.string().max(100).optional(),
		status: z.enum(['all', 'OPEN', 'CLOSED']).optional(),
		name: z.string().max(200).optional(),
		description: z.string().max(200).optional(),
		category: z.string().max(200).optional(),
		finance_account_ids: z.array(z.number().int().positive()).max(200).optional()
	})
	.strict()
	.refine(filters => filters.start_date <= filters.end_date, { message: END_BEFORE_START_MESSAGE, path: ['end_date'] });
export type CashFlowExportFilters = z.infer<typeof CashFlowExportFiltersSchema>;

/** Mesmos filtros que a tela do Portal de NFe manda para `/api/nfes/pdf|xls`. O período é obrigatório, como em toda exportação de relatório financeiro (MF-1143). */
export const NfeExportFiltersSchema = z
	.object({
		nfe_type: z.enum(['0', '1']).optional(),
		status: z.string().max(25).optional(),
		emmiter_name: z.string().max(200).optional(),
		start_date: isoDate,
		end_date: isoDate
	})
	.strict()
	.refine(filters => filters.start_date <= filters.end_date, {
		message: END_BEFORE_START_MESSAGE,
		path: ['end_date']
	});
export type NfeExportFilters = z.infer<typeof NfeExportFiltersSchema>;

/** Mesmos filtros que a tela de Orçamentos manda para `/api/reports/budget/pdf|xls`, sem paginação. */
export const ReportBudgetExportFiltersSchema = z
	.object({
		budget_type: ReportBudgetFilterTypeEnum.optional(),
		start_date: isoDate,
		end_date: isoDate,
		patient_name: z.string().max(200).optional(),
		responsible_name: z.string().max(200).optional(),
		patient_id: z.number().int().positive().optional(),
		user_id: z.number().int().positive().optional()
	})
	.strict()
	.refine(filters => filters.start_date <= filters.end_date, {
		message: END_BEFORE_START_MESSAGE,
		path: ['end_date']
	});
export type ReportBudgetExportFilters = z.infer<typeof ReportBudgetExportFiltersSchema>;

/** Mesmos filtros que a tela de Planejamentos manda para `/api/reports/hof-patient-plans/pdf|xls`, sem paginação. */
export const PlansExportFiltersSchema = z
	.object({
		type: z.string().max(20).optional(),
		category: z.string().max(200).optional(),
		status: z.string().max(40).optional(),
		payment_method: z.string().max(100).optional(),
		start_date: isoDate,
		end_date: isoDate,
		patient_id: z.number().int().positive().optional(),
		patient_name: z.string().max(200).optional(),
		procedure_name: z.string().max(200).optional(),
		position: z.string().max(200).optional()
	})
	.strict()
	.refine(filters => filters.start_date <= filters.end_date, {
		message: END_BEFORE_START_MESSAGE,
		path: ['end_date']
	});
export type PlansExportFilters = z.infer<typeof PlansExportFiltersSchema>;

/** Mesmos filtros que a tela do Livro Caixa manda para `/api/finance/pdf|xls`, sem paginação. */
export const FinanceHistoryExportFiltersSchema = z
	.object({
		type: z.string().max(20).optional(),
		category: z.string().max(200).optional(),
		payment_method: z.string().max(100).optional(),
		finance_account_id: z.coerce.number().int().positive().optional(),
		name: z.string().max(200).optional(),
		description: z.string().max(200).optional(),
		start_date: isoDate,
		end_date: isoDate
	})
	.strict()
	.refine(filters => filters.start_date <= filters.end_date, {
		message: END_BEFORE_START_MESSAGE,
		path: ['end_date']
	});
export type FinanceHistoryExportFilters = z.infer<typeof FinanceHistoryExportFiltersSchema>;

/**
 * Mesmos filtros que as telas de Contas a Receber e Contas a Pagar mandam para `/api/finance/provisions/pdf|xls`,
 * sem paginação. O `type` (INCOME/EXPENSE) não vem do cliente: é fixado pelo tipo do relatório.
 */
export const FinanceProvisionExportFiltersSchema = z
	.object({
		category: z.string().max(200).optional(),
		status: z.string().max(40).optional(),
		payment_method: z.string().max(100).optional(),
		name: z.string().max(200).optional(),
		description: z.string().max(200).optional(),
		start_date: isoDate,
		end_date: isoDate
	})
	.strict()
	.refine(filters => filters.start_date <= filters.end_date, {
		message: END_BEFORE_START_MESSAGE,
		path: ['end_date']
	});
export type FinanceProvisionExportFilters = z.infer<typeof FinanceProvisionExportFiltersSchema>;

/** Mesmos filtros que a tela de Produção / Comissão manda para `/api/reports/transfers/pdf|xls`, sem paginação. */
export const TransfersExportFiltersSchema = z
	.object({
		type: z.enum(['finances', 'plans', 'budget_v2']).optional(),
		category: z.string().max(200).optional(),
		status: z.string().max(40).optional(),
		payment_method: z.string().max(100).optional(),
		start_date: isoDate,
		end_date: isoDate,
		patient_name: z.string().max(200).optional(),
		procedure_name: z.string().max(200).optional(),
		position: z.string().max(200).optional(),
		// O model compara `String(user_id) === filter.user_id`: o profissional chega como texto, como na query string
		user_id: z.string().regex(/^\d+$/).optional()
	})
	.strict()
	.refine(filters => filters.start_date <= filters.end_date, {
		message: END_BEFORE_START_MESSAGE,
		path: ['end_date']
	});
export type TransfersExportFilters = z.infer<typeof TransfersExportFiltersSchema>;

/**
 * Mesmos filtros que a tela de Procedimentos manda para as rotas `pdf|xls` da origem escolhida (`type`). Status e
 * buscas por nome são filtros locais da tela e nunca chegaram à exportação.
 */
export const ProceduresExportFiltersSchema = z
	.object({
		type: z.enum(['plans', 'appointments', 'budget_v2']),
		// Procedimentos sem data lista também os que não têm validade (decisão de 2026-10-19): período opcional
		expiration_start_date: isoDate.optional(),
		expiration_end_date: isoDate.optional()
	})
	.strict()
	.refine(filters => !filters.expiration_start_date || !filters.expiration_end_date || filters.expiration_start_date <= filters.expiration_end_date, {
		message: END_BEFORE_START_MESSAGE,
		path: ['expiration_end_date']
	});
export type ProceduresExportFilters = z.infer<typeof ProceduresExportFiltersSchema>;

export const ReportExportJobSchema = z.object({
	id: z.number().int(),
	report_type: z.enum(REPORT_EXPORT_TYPES),
	format: z.enum(REPORT_EXPORT_FORMATS),
	status: z.enum(REPORT_EXPORT_STATUSES),
	step: z.enum(REPORT_EXPORT_STEPS).nullable(),
	attempts: z.number().int(),
	max_attempts: z.number().int(),
	next_attempt_at: z.string().nullable(),
	retry_of_job_id: z.number().int().nullable(),
	filters: z.record(z.string(), z.unknown()),
	/** Os filtros pessoais já foram removidos pelo prazo de retenção; a tarefa não pode mais ser gerada de novo. */
	filters_redacted: z.boolean(),
	file_name: z.string().nullable(),
	row_count: z.number().int().nullable(),
	file_expires_at: z.string().nullable(),
	data_reference_at: z.string().nullable(),
	error_code: z.string().nullable(),
	error_message: z.string().nullable(),
	created_at: z.string(),
	started_at: z.string().nullable(),
	finished_at: z.string().nullable(),
	cancel_requested_at: z.string().nullable()
});
export type ReportExportJob = z.infer<typeof ReportExportJobSchema>;
